import { useCallback, useMemo, useState } from 'react';

import type { FieldConfig } from './useFormErrors';
import { useFormErrors } from './useFormErrors';

export type { FieldConfig };

interface FieldProps {
  onBlur: () => void;
  error: string | undefined;
  showError: boolean;
}

interface UseFormValidationReturn<K extends string = string> {
  errors: Record<K, string | undefined>;
  visibleErrors: Record<K, string | undefined>;
  touchAll: () => void;
  isValid: boolean;
  getFieldProps: (name: K) => FieldProps;
  touched: ReadonlySet<K>;
}

export function useFormValidation<T extends Record<string, unknown>>({
  fields,
  values,
}: {
  fields: { [K in keyof T]: FieldConfig };
  values: T;
}): UseFormValidationReturn<keyof T & string> {
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const { errors, isValid } = useFormErrors({ fields, values });

  const visibleErrors = useMemo<Record<string, string | undefined>>(() => {
    const result: Record<string, string | undefined> = {};
    for (const name of Object.keys(errors)) {
      if (touched.has(name)) {
        result[name] = errors[name];
      }
    }
    return result;
  }, [errors, touched]);

  const touchAll = useCallback(() => {
    setTouched(new Set(Object.keys(fields)));
  }, [fields]);

  const getFieldProps = useCallback(
    (name: string): FieldProps => {
      const error = errors[name];
      return {
        onBlur: () => {
          setTouched(prev => {
            if (prev.has(name)) {
              return prev;
            }
            const next = new Set(prev);
            next.add(name);
            return next;
          });
        },
        error,
        showError: touched.has(name) && !!error,
      };
    },
    [errors, touched]
  );

  return {
    errors,
    visibleErrors,
    touchAll,
    isValid,
    getFieldProps,
    touched,
  } as unknown as UseFormValidationReturn<keyof T & string>;
}
